import { AxiosResponse } from "axios";
import {
  AltiusRes,
  ICreateRound,
  IDealRoundsWithInvestors,
  IGetRounds,
  IRound,
  IUpdateDealRoundsWithInvestors,
  IUpdateRound,
  Optional
} from "store/types";
import toIsoMidday from "utils/to-iso-midday";
import { DealAction } from "..";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IRoundActions {
  createRoundAction: (data: ICreateRound) => Promise<IRound>;
  deleteRoundAction: (id: number) => Promise<IRound>;
  updateRoundAction: (data: IUpdateRound) => Promise<IRound>;

  getRoundsAction: <T extends boolean>(
    data: IGetRounds<T>
  ) => Promise<T extends true ? IRound[] : Optional<IRound, "deal_commitments">[]>;
  getDealRoundsWithInvestorsAction: (data: {
    dealId: number;
    with_parent_round?: boolean;
    by_investors?: boolean;
  }) => Promise<IDealRoundsWithInvestors[]>;
  updateDealRoundsInvestorsAction: (data: IUpdateDealRoundsWithInvestors) => Promise<void>;
  createDealRoundInvestors: (dealId: number, roundId: number, investors: number[]) => Promise<void>;
  manageDealRoundsAction: (payload: {
    [dealId: string]: { id: number | string; name: string; is_deleted: boolean }[];
  }) => Promise<void>;
}

export class RoundActions extends AsyncActions implements IRoundActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public createRoundAction = async (data: ICreateRound): Promise<IRound> => {
    try {
      const { dealId, name, date } = data;
      const {
        data: { data: payload }
      }: AxiosResponse<AltiusRes<IRound>> = await this.api.post({
        url: "/api/v0.0.3/rounds",
        data: {
          deal_id: dealId,
          name,
          date
        }
      });
      this.dispatch({ type: DealAction.CreateRoundSuccess, payload });
      return payload;
    } catch (error) {
      throw error;
    }
  };

  public deleteRoundAction = async (id: number): Promise<IRound> => {
    try {
      const {
        data: { data: payload }
      }: AxiosResponse<AltiusRes<IRound>> = await this.api.delete({
        url: `/api/v0.0.3/rounds/${id}`
      });
      this.dispatch({ type: DealAction.DeleteRoundSuccess, payload });
      return payload;
    } catch (error) {
      throw error;
    }
  };

  public updateRoundAction = async ({ id, ...body }: IUpdateRound): Promise<IRound> => {
    try {
      const bodyCopy: { [key: string]: any } = { ...body };

      if (bodyCopy.date) {
        bodyCopy.date = toIsoMidday(new Date(bodyCopy.date).toISOString());
      }

      const {
        data: { data: payload }
      }: AxiosResponse<AltiusRes<IRound>> = await this.api.patch({
        url: `/api/v0.0.3/rounds/${id}`,
        data: bodyCopy
      });
      this.dispatch({ type: DealAction.UpdateRoundSuccess, payload });
      return payload;
    } catch (error) {
      throw error;
    }
  };

  public getRoundsAction = async <T extends boolean>({
    with_commitments,
    dealId,
    dispatch
  }: IGetRounds<T>): Promise<T extends true ? IRound[] : Optional<IRound, "deal_commitments">[]> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<T extends true ? IRound[] : Optional<IRound, "deal_commitments">[]>>({
      url: `/api/v0.0.3/deals/${dealId}/rounds`,
      params: {
        with_commitments
      }
    });
    if (dispatch && payload[0]?.deal_commitments?.length) {
      this.dispatch({ type: DealAction.GetRoundsSuccess, payload });
    }
    return payload;
  };

  public getDealRoundsWithInvestorsAction: IRoundActions["getDealRoundsWithInvestorsAction"] = async ({
    dealId,
    ...params
  }) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IDealRoundsWithInvestors[]>>({
      url: `/api/v0.0.3/deals/${dealId}/round-investors`,
      params
    });
    return data;
  };

  public updateDealRoundsInvestorsAction = async (data: IUpdateDealRoundsWithInvestors) => {
    try {
      await this.api.post({
        url: `/api/v0.0.3/deals/${data.dealId}/round-investors`,
        data: { data: [...data.rounds] }
      });
    } catch (error) {
      throw error;
    }
  };

  public createDealRoundInvestors = async (dealId: number, roundId: number, investors: number[]) => {
    await this.api.post({
      url: `/api/v0.0.3/deals/${dealId}/add-round-investors`,
      data: {
        round_id: roundId,
        investors
      }
    });
  };

  public manageDealRoundsAction: IRoundActions["manageDealRoundsAction"] = async (data) => {
    await this.api.post({
      url: "/api/v0.0.3/manage-rounds",
      data: {
        roundsByDeals: data
      }
    });
  };
}
